import { useRouter } from 'next/router';

import { Alert, Box, Icon, List, Text, TextButton } from '@zillow/constellation-10';
import { IconLightBulbFilled } from '@zillow/constellation-icons';

import { useGetListingDetails, useGetListingRecommendations } from '../../../hooks/queries/properties';

import { RECS, RECS_ARRAY_ORDER } from '../../../constants/RecommendationsConstants';

import { emitLearnMoreEvent, EVENT_IDS } from '../../../analytics/events/add3DTours';
import { useAuth } from '../../AuthProvider';

import type { REVIEW_PAGE_SECTIONS } from '../../../constants/ListingDetailConstants';

type RecommendationsAlertProps = {
  sectionName: keyof typeof REVIEW_PAGE_SECTIONS;
  onRecsClick?: (recKey: string) => void;
  showLearnMore?: boolean;
};

const RecommendationsAlert = ({
  sectionName,
  onRecsClick = () => {},
  showLearnMore = true,
}: RecommendationsAlertProps) => {
  const router = useRouter();
  const { user } = useAuth();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { recommendationsBySection } = useGetListingRecommendations({
    listingAlias,
    isMultifamily: listingDetails?.isMultifamily,
  });

  if (!recommendationsBySection?.[sectionName]) {
    return null;
  }

  const unsortedRecKeys = Object.keys(recommendationsBySection[sectionName]);

  if (unsortedRecKeys.length === 0) {
    return null;
  }

  const enforceSortedKeys = RECS_ARRAY_ORDER.hasOwnProperty(sectionName);
  const sortedRecKeys: Array<string> = [];

  if (enforceSortedKeys && sectionName in RECS_ARRAY_ORDER) {
    unsortedRecKeys.forEach((recKey) => {
      const order = RECS_ARRAY_ORDER[sectionName as keyof typeof RECS_ARRAY_ORDER] as Record<string, number>;
      sortedRecKeys[order[recKey]] = recKey;
    });
  }

  const keysToIterateOver = enforceSortedKeys ? sortedRecKeys.filter(Boolean) : unsortedRecKeys;

  return (
    <Box css={{ marginBottom: '400' }}>
      <Alert.Root
        tone="info"
        css={{
          // eslint-disable-next-line curly-quotes/no-straight-quotes
          '& > [data-c11n-component="Alert.Icon"]': {
            display: 'none',
          },
        }}
      >
        <Box
          css={{
            display: 'flex',
            flexShrink: 0,
            alignItems: 'flex-start',
            marginInlineEnd: '300',
          }}
        >
          <Icon render={<IconLightBulbFilled />} size="md" css={{ color: 'bg.feedback.info.hero' }} />
        </Box>

        <Alert.Body>
          {keysToIterateOver.length === 1 ? (
            <>
              {/* @ts-expect-error `text` exists on BaseStyledComponentDto*/}
              <Text>{recommendationsBySection[sectionName][keysToIterateOver[0]].learnMore.title.text}</Text>

              {showLearnMore && (
                <TextButton
                  css={{ marginLeft: '200' }}
                  data-eventid={keysToIterateOver[0] === RECS.VIRTUAL_TOUR ? EVENT_IDS.LEARN_MORE : undefined}
                  onClick={() => {
                    if (keysToIterateOver[0] === RECS.VIRTUAL_TOUR && listingDetails && user) {
                      emitLearnMoreEvent({ currentProperty: listingDetails, user });
                    }
                    onRecsClick(keysToIterateOver[0]);
                  }}
                >
                  Learn more
                </TextButton>
              )}
            </>
          ) : (
            <List.Root>
              {keysToIterateOver.map((recKey, idx) => (
                <List.Item key={idx} css={{ paddingBlock: '0' }}>
                  {/* @ts-expect-error `text` exists on BaseStyledComponentDto*/}
                  <Text>{recommendationsBySection[sectionName][recKey].learnMore.title.text}</Text>

                  <TextButton
                    css={{ marginLeft: '200' }}
                    data-eventid={recKey === RECS.VIRTUAL_TOUR ? EVENT_IDS.LEARN_MORE : undefined}
                    onClick={() => {
                      if (recKey === RECS.VIRTUAL_TOUR && listingDetails && user) {
                        emitLearnMoreEvent({ currentProperty: listingDetails, user });
                      }
                      onRecsClick(recKey);
                    }}
                    aria-label={`Learn more about ${recKey}`}
                  >
                    Learn more
                  </TextButton>
                </List.Item>
              ))}
            </List.Root>
          )}
        </Alert.Body>
      </Alert.Root>
    </Box>
  );
};

export default RecommendationsAlert;
